import { FC } from 'react';
import { useQuery } from 'react-query';
import AudienceRestrictionsApi, {
  AudiencePreset,
} from 'app/modules/posts/api/audienceRestrictions';
import { Alert, Button, TextLink } from '@kontentino/ui';
import { AudienceRestrictions } from 'app/modules/posts/types/AudienceRestrictionsOptions';
import Skeleton from 'react-loading-skeleton';
import { transformPresetForPageType } from '../utils/presetTransformers';
import { queryKey } from 'constants/queryKey';
import { useTranslation } from 'react-i18next';

type Props = {
  onPresetSelect: (audienceRestrictions: AudienceRestrictions) => void;
  pageType: number;
};

const AudienceRestrictionsPresets: FC<Props> = ({
  onPresetSelect,
  pageType,
}) => {
  const { t } = useTranslation();
  const {
    data: presets,
    isLoading,
    isError,
    isFetching,
    error,
  } = useQuery<AudiencePreset[], Error>(
    queryKey.audiencePresets(pageType),
    () => AudienceRestrictionsApi.getPresets(pageType),
    {
      enabled: !!pageType,
    },
  );

  const isLoadingData = isLoading || isFetching;

  const handlePresetClick = (preset: AudiencePreset) => {
    const transformedRestrictions = transformPresetForPageType(
      preset,
      pageType,
    );
    onPresetSelect(transformedRestrictions);
  };

  if (isError) {
    return (
      <Alert variant="danger">
        {error?.message || t('audienceRestrictions.failedToLoadPresets')}
      </Alert>
    );
  }

  return (
    <div>
      <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-3">
        {!isLoadingData && presets?.length === 0 && (
          <p className="tw-text-gray-500 tw-text-sm">
            {t('audienceRestrictions.noPresetsFound')}
          </p>
        )}
        {isLoadingData && <Skeleton count={3} height={40} />}
        {!isLoadingData &&
          presets?.map((preset) => (
            <div
              key={preset.id}
              className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-bg-grayscale-10 tw-p-2 tw-text-sm hover:tw-bg-opacity-75 "
            >
              {preset.name}

              <TextLink>
                <Button
                  variant="plain"
                  onClick={() => handlePresetClick(preset)}
                  className="tw-min-w-[100px]"
                  disabled={isLoadingData}
                >
                  {t('audienceRestrictions.applyPreset')}
                </Button>
              </TextLink>
            </div>
          ))}
      </div>
    </div>
  );
};

export default AudienceRestrictionsPresets;
